import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, query, where } from 'firebase/firestore';
import { db, getCompanyId } from '../firebase';
import Navbar from '../components/Navbar';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const TIMES = Array.from({ length: 24 * 4 }, (_, i) => {
  const totalMinutes = i * 15;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  const displayMinute = String(minute).padStart(2, '0');
  return { label: `${displayHour}:${displayMinute} ${ampm}`, value: i, hour, minute };
});

// Shifts may end at midnight (value 96)
const END_TIMES = [...TIMES.slice(1), { label: '12:00 AM', value: 96, hour: 24, minute: 0 }];
const timeLabel = (v) => (v === 96 ? '12:00 AM' : TIMES[v]?.label);

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Parse 'YYYY-MM-DD' as a local date (new Date(str) would treat it as UTC)
const parseDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Monday-first weekday label for a Date
const weekdayLabel = (d) => DAYS[(d.getDay() + 6) % 7];

const prettyDate = (s) => {
  const d = parseDate(s);
  return `${weekdayLabel(d)}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

export default function EmployeeSchedule() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  // Inline edit of a one-time shift (name + times)
  const [editingShift, setEditingShift] = useState(null);
  const [editName, setEditName] = useState('');
  const [editStart, setEditStart] = useState(36);
  const [editEnd, setEditEnd] = useState(68);

  // "Put this employee on a shift template for the selected day" picker
  const [showNew, setShowNew] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    const companyId = await getCompanyId();
    const [empSnap, shiftsSnap] = await Promise.all([
      getDoc(doc(db, 'users', id)),
      getDocs(query(collection(db, 'shifts'), where('companyId', '==', companyId))),
    ]);
    // Only show employees belonging to this admin's company
    if (!empSnap.exists() || empSnap.data().companyId !== companyId || empSnap.data().role !== 'employee') {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setEmployee({ id: empSnap.id, ...empSnap.data() });
    setShifts(shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const closeForms = () => {
    setEditingShift(null);
    setShowNew(false);
    setError('');
  };

  // Shifts on a date, split into ones this employee works and ones they could be added to
  const shiftsOnDate = (dateStr) => {
    const day = weekdayLabel(parseDate(dateStr));
    const onDate = shifts.filter(s =>
      (s.type === 'one-time' && s.date === dateStr) ||
      (s.type === 'repeating' && (s.employeesByDay || {})[day])
    );
    const isMine = (s) => s.type === 'one-time'
      ? (s.employeeIds || []).includes(id)
      : ((s.employeesByDay || {})[day] || []).includes(id);
    return {
      mine: onDate.filter(isMine),
      available: onDate.filter(s => !isMine(s)),
    };
  };

  const handleDayClick = (dateStr) => {
    closeForms();
    setSelectedDate(selectedDate === dateStr ? null : dateStr);
  };

  const changeMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const handleAssign = async (shift) => {
    setError('');
    try {
      if (shift.type === 'one-time') {
        await updateDoc(doc(db, 'shifts', shift.id), {
          employeeIds: [...(shift.employeeIds || []), id],
          updatedAt: new Date(),
        });
      } else {
        const day = weekdayLabel(parseDate(selectedDate));
        if (!window.confirm(`Add ${employee.name} to "${shift.name}" every ${day}?`)) return;
        const byDay = { ...(shift.employeesByDay || {}) };
        byDay[day] = [...(byDay[day] || []), id];
        await updateDoc(doc(db, 'shifts', shift.id), { employeesByDay: byDay, updatedAt: new Date() });
      }
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnassign = async (shift) => {
    setError('');
    try {
      if (shift.type === 'one-time') {
        if (!window.confirm(`Remove ${employee.name} from "${shift.name}" on ${prettyDate(shift.date)}?`)) return;
        await updateDoc(doc(db, 'shifts', shift.id), {
          employeeIds: (shift.employeeIds || []).filter(e => e !== id),
          updatedAt: new Date(),
        });
      } else {
        const day = weekdayLabel(parseDate(selectedDate));
        if (!window.confirm(`Remove ${employee.name} from "${shift.name}" on every ${day}?`)) return;
        const byDay = { ...(shift.employeesByDay || {}) };
        byDay[day] = (byDay[day] || []).filter(e => e !== id);
        await updateDoc(doc(db, 'shifts', shift.id), { employeesByDay: byDay, updatedAt: new Date() });
      }
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openEdit = (shift) => {
    closeForms();
    setEditingShift(shift);
    setEditName(shift.name);
    setEditStart(shift.startTime ?? 36);
    setEditEnd(shift.endTime ?? 68);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError('');
    if (!editName) { setError('Please enter a shift name'); return; }
    if (editEnd <= editStart) { setError('End time must be after start time'); return; }
    setSaving(true);
    try {
      const end = END_TIMES.find(t => t.value === editEnd);
      await updateDoc(doc(db, 'shifts', editingShift.id), {
        name: editName,
        startTime: editStart,
        endTime: editEnd,
        startHour: TIMES[editStart].hour,
        startMinute: TIMES[editStart].minute,
        endHour: end.hour,
        endMinute: end.minute,
        updatedAt: new Date(),
      });
      await loadData();
      closeForms();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const openNew = () => {
    closeForms();
    setShowNew(true);
  };

  // Schedule an existing template on this day with this employee already on
  // it. (Shifts themselves are only created in the Shifts tab.)
  const handleAddFromTemplate = async (template) => {
    setError('');
    setSaving(true);
    try {
      const companyId = await getCompanyId();
      await addDoc(collection(db, 'shifts'), {
        name: template.name || '',
        type: 'one-time',
        date: selectedDate,
        shiftTemplateId: template.id,
        startTime: template.startTime,
        endTime: template.endTime,
        startHour: template.startHour,
        startMinute: template.startMinute,
        endHour: template.endHour,
        endMinute: template.endMinute,
        employeeIds: [id],
        employeesByDay: null,
        companyId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      await loadData();
      closeForms();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  // Build the month grid: leading blanks (Monday-first), then each day of the month
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
  const cells = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => fmtDate(new Date(viewYear, viewMonth, i + 1))),
  ];

  const todayStr = fmtDate(today);
  const selectedDay = selectedDate ? shiftsOnDate(selectedDate) : null;
  const templates = shifts.filter(s => s.type === 'template');
  // Templates not yet scheduled on the selected day. (Already-scheduled ones
  // show under "mine" or "other shifts on this day" instead.)
  const addableTemplates = selectedDate
    ? templates.filter(t => !shifts.some(s =>
        s.type === 'one-time' && s.date === selectedDate && s.shiftTemplateId === t.id))
    : [];

  if (notFound) {
    return (
      <div style={styles.page}>
        <Navbar />
        <div style={styles.container}>
          <div style={styles.empty}>Employee not found.</div>
          <button style={styles.backBtn} onClick={() => navigate('/employees')}>← Back to Employees</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.container}>
        <button style={styles.backBtn} onClick={() => navigate('/employees')}>← Back to Employees</button>

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>{loading ? 'Loading...' : `${employee.name}'s Schedule`}</h1>
            {!loading && <div style={styles.subtitle}>{employee.email}</div>}
          </div>
        </div>

        <div style={styles.calendarCard}>
          <div style={styles.calHeader}>
            <div style={styles.calTitle}>{MONTHS[viewMonth]} {viewYear}</div>
            <div style={styles.calNav}>
              <button style={styles.calNavBtn} onClick={() => changeMonth(-1)}>‹</button>
              <button style={styles.calTodayBtn} onClick={goToday}>Today</button>
              <button style={styles.calNavBtn} onClick={() => changeMonth(1)}>›</button>
            </div>
          </div>

          <div style={styles.weekdayRow}>
            {DAYS.map(d => <div key={d} style={styles.weekdayCell}>{d}</div>)}
          </div>

          {loading ? (
            <div style={styles.empty}>Loading...</div>
          ) : (
            <div style={styles.grid}>
              {cells.map((dateStr, i) => {
                if (!dateStr) return <div key={`blank-${i}`} style={styles.blankCell} />;
                const { mine } = shiftsOnDate(dateStr);
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDate;
                const isPast = dateStr < todayStr;
                return (
                  <div
                    key={dateStr}
                    style={{
                      ...styles.dayCell,
                      ...(isPast ? styles.dayCellPast : {}),
                      ...(isSelected ? styles.dayCellSelected : {}),
                    }}
                    onClick={() => handleDayClick(dateStr)}
                  >
                    <div style={{ ...styles.dayNumber, ...(isToday ? styles.dayNumberToday : {}) }}>
                      {Number(dateStr.slice(8))}
                    </div>
                    {mine.slice(0, 3).map((s, idx) => (
                      <div
                        key={s.id + idx}
                        style={{ ...styles.shiftChip, ...(s.type === 'repeating' ? styles.shiftChipRepeating : {}) }}
                      >
                        {s.name}
                      </div>
                    ))}
                    {mine.length > 3 && <div style={styles.moreChip}>+{mine.length - 3} more</div>}
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.legend}>
            <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#4f46e5' }} /> Scheduled shift</span>
            <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#2a2a4a', border: '1px solid #4f46e5' }} /> Legacy weekly shift</span>
          </div>
        </div>

        {selectedDate && selectedDay && (
          <div style={styles.dayPanel}>
            <div style={styles.dayPanelHeader}>
              <h3 style={styles.dayPanelTitle}>{prettyDate(selectedDate)}</h3>
              <button style={styles.smallAddBtn} onClick={openNew}>
                + Put {loading ? 'employee' : employee.name.split(' ')[0]} on a shift
              </button>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {showNew && (
              <div style={styles.inlineForm}>
                <div style={styles.inlineFormTitle}>
                  Schedule a shift template on {prettyDate(selectedDate)} with {employee?.name} on it.
                  Shifts are created in the Shifts tab.
                </div>
                {addableTemplates.length === 0 ? (
                  <div style={styles.inlineFormTitle}>
                    {templates.length === 0
                      ? 'No shift templates yet — create one in the Shifts tab first.'
                      : 'All templates are already scheduled on this day — use the sections below to assign.'}
                  </div>
                ) : (
                  <div style={styles.inlineFormRow}>
                    {addableTemplates.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        style={styles.templateChip}
                        disabled={saving}
                        onClick={() => handleAddFromTemplate(t)}
                      >
                        + {t.name || `${timeLabel(t.startTime)} → ${timeLabel(t.endTime)}`}
                        {t.name && <span style={styles.templateChipTime}> · {timeLabel(t.startTime)} → {timeLabel(t.endTime)}</span>}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  <button type="button" style={styles.cancelBtn} onClick={closeForms}>Close</button>
                </div>
              </div>
            )}

            {selectedDay.mine.length === 0 && !showNew && (
              <div style={styles.dayPanelEmpty}>
                {loading ? '' : `${employee.name} has no shifts on this day.`}
              </div>
            )}

            {selectedDay.mine.map(shift => (
              <div key={shift.id}>
                <div style={styles.dayShiftRow}>
                  <div style={styles.dayShiftInfo}>
                    <div style={styles.dayShiftName}>
                      {shift.name}
                      {shift.type === 'repeating' && <span style={styles.repeatBadge}>Weekly</span>}
                    </div>
                    <div style={styles.dayShiftMeta}>
                      {timeLabel(shift.startTime)} → {timeLabel(shift.endTime)}
                    </div>
                  </div>
                  <div style={styles.actions}>
                    {shift.type === 'one-time' && (
                      <button style={styles.editBtn} onClick={() => openEdit(shift)}>Edit</button>
                    )}
                    <button style={styles.deleteBtn} onClick={() => handleUnassign(shift)}>
                      {shift.type === 'repeating'
                        ? `Remove from ${weekdayLabel(parseDate(selectedDate))}s`
                        : 'Remove'}
                    </button>
                  </div>
                </div>

                {editingShift && editingShift.id === shift.id && (
                  <form onSubmit={handleSaveEdit} style={styles.inlineForm}>
                    <div style={styles.inlineFormTitle}>
                      Edit shift
                      {(shift.employeeIds || []).length > 1 &&
                        ` — changes apply to all ${(shift.employeeIds || []).length} assigned employees`}
                    </div>
                    <div style={styles.inlineFormRow}>
                      <input
                        style={styles.input}
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                      />
                      <select style={styles.select} value={editStart} onChange={e => setEditStart(Number(e.target.value))}>
                        {TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <span style={{ color: '#999', fontSize: 18, padding: '0 4px' }}>→</span>
                      <select style={styles.select} value={editEnd} onChange={e => setEditEnd(Number(e.target.value))}>
                        {END_TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <button type="submit" style={styles.saveBtn} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" style={styles.cancelBtn} onClick={closeForms}>Cancel</button>
                    </div>
                  </form>
                )}
              </div>
            ))}

            {selectedDay.available.length > 0 && (
              <div style={styles.availableSection}>
                <div style={styles.availableTitle}>Other shifts on this day — add {loading ? '' : employee.name.split(' ')[0]}:</div>
                {selectedDay.available.map(shift => (
                  <div key={shift.id} style={styles.dayShiftRow}>
                    <div style={styles.dayShiftInfo}>
                      <div style={styles.dayShiftName}>
                        {shift.name}
                        {shift.type === 'repeating' && <span style={styles.repeatBadge}>Weekly</span>}
                      </div>
                      <div style={styles.dayShiftMeta}>
                        {timeLabel(shift.startTime)} → {timeLabel(shift.endTime)}
                      </div>
                    </div>
                    <div style={styles.actions}>
                      <button style={styles.assignBtn} onClick={() => handleAssign(shift)}>
                        {shift.type === 'repeating'
                          ? `+ Assign every ${weekdayLabel(parseDate(selectedDate))}`
                          : '+ Assign'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!selectedDate && !loading && (
          <div style={styles.hint}>Click a day on the calendar to view or edit {employee.name}'s shifts.</div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0f0f0f'
  },

  container: {
    padding: '32px 40px',
    maxWidth: 1200,
    margin: '0 auto'
  },

  backBtn: {
    backgroundColor: 'transparent',
    color: '#999',
    border: 'none',
    padding: 0,
    marginBottom: 16,
    cursor: 'pointer',
    fontSize: 14
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24
  },

  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    margin: 0
  },

  subtitle: {
    color: '#999',
    fontSize: 14,
    marginTop: 4
  },

  calendarCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24
  },

  calHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },

  calTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold'
  },

  calNav: {
    display: 'flex',
    gap: 8
  },

  calNavBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    width: 36,
    height: 36,
    fontSize: 18,
    cursor: 'pointer'
  },

  calTodayBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '0 16px',
    fontSize: 13,
    fontWeight: 'bold',
    cursor: 'pointer'
  },

  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 6,
    marginBottom: 6
  },

  weekdayCell: {
    color: '#999',
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
    padding: '6px 0'
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 6
  },

  blankCell: {
    minHeight: 96
  },

  dayCell: {
    backgroundColor: '#2a2a2a',
    borderRadius: 10,
    minHeight: 96,
    padding: 8,
    cursor: 'pointer',
    border: '1px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    overflow: 'hidden'
  },

  dayCellPast: {
    opacity: 0.5
  },

  dayCellSelected: {
    border: '1px solid #fff'
  },

  dayNumber: {
    color: '#fff',
    fontSize: 13,
    fontWeight: 'bold',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%'
  },

  dayNumberToday: {
    backgroundColor: '#4f46e5'
  },

  shiftChip: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    borderRadius: 6,
    padding: '2px 6px',
    fontSize: 11,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  shiftChipRepeating: {
    backgroundColor: '#2a2a4a',
    border: '1px solid #4f46e5',
    color: '#c7c5f4'
  },

  moreChip: {
    color: '#999',
    fontSize: 11,
    fontWeight: 'bold'
  },

  legend: {
    display: 'flex',
    gap: 20,
    marginTop: 14
  },

  legendItem: {
    color: '#999',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },

  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 4,
    display: 'inline-block'
  },

  dayPanel: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24
  },

  dayPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 10
  },

  dayPanelTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    margin: 0
  },

  smallAddBtn: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 'bold',
    cursor: 'pointer'
  },

  dayPanelEmpty: {
    color: '#999',
    fontSize: 14,
    padding: '8px 0'
  },

  error: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14
  },

  inlineForm: {
    backgroundColor: '#2a2a2a',
    borderRadius: 10,
    padding: 16,
    marginBottom: 12
  },

  inlineFormTitle: {
    color: '#999',
    fontSize: 13,
    marginBottom: 10
  },

  inlineFormRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap'
  },

  input: {
    backgroundColor: '#1e1e1e',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    flex: 1,
    minWidth: 160
  },

  select: {
    backgroundColor: '#1e1e1e',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer'
  },

  saveBtn: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13
  },

  cancelBtn: {
    backgroundColor: '#1e1e1e',
    color: '#999',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13
  },

  templateChip: {
    backgroundColor: '#1e1e1e',
    color: '#fff',
    border: '1px dashed #4f46e5',
    borderRadius: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: 'inherit'
  },

  templateChipTime: {
    color: '#999',
    fontWeight: 'normal',
    fontSize: 13
  },

  dayShiftRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    padding: '12px 0',
    borderBottom: '1px solid #2a2a2a',
    flexWrap: 'wrap'
  },

  dayShiftInfo: {
    flex: 1,
    minWidth: 220
  },

  dayShiftName: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 4
  },

  dayShiftMeta: {
    color: '#999',
    fontSize: 13
  },

  repeatBadge: {
    color: '#4f46e5',
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 6
  },

  actions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },

  editBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13
  },

  deleteBtn: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13
  },

  assignBtn: {
    backgroundColor: '#14291a',
    color: '#4ade80',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 'bold'
  },

  availableSection: {
    marginTop: 20
  },

  availableTitle: {
    color: '#999',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4
  },

  hint: {
    color: '#999',
    fontSize: 14,
    textAlign: 'center',
    padding: 20
  },

  empty: {
    color: '#999',
    textAlign: 'center',
    padding: 40
  }
};
